import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { api, money, newId, type SentryIssue } from "../api";
import { Status } from "./RunList";

/**
 * One project: what it is, its Sentry issues, and every run against it.
 *
 * The two tabs answer the two questions an operator has — "what is broken?" and
 * "what has the engine done about it?" — and the Auto Heal button is the join
 * between them.
 */
export function ProjectDetail() {
  const { projectId = "" } = useParams();
  const [tab, setTab] = useState<"issues" | "runs">("issues");
  const project = useQuery({
    queryKey: ["project", projectId],
    queryFn: () => api.project(projectId),
  });

  if (project.isPending) return <p className="muted">Loading project…</p>;
  if (project.error) return <p className="error">{project.error.message}</p>;

  const p = project.data;

  return (
    <article>
      <p>
        <Link to="/projects">← All projects</Link>
      </p>
      <h1>{p.repo}</h1>
      <p className="muted">
        {p.repo_url ?? "(deployment-wide forge)"} · {p.default_branch} ·{" "}
        <span className={`status status-mode-${p.write_mode}`}>{p.write_mode}</span>{" "}
        {!p.enabled ? <span className="badge">disabled</span> : null}
      </p>
      <dl className="cost">
        <div>
          <dt>Nightly budget</dt>
          <dd>{money(p.nightly_budget_usd)}</dd>
        </div>
        <div>
          <dt>Issues per night</dt>
          <dd>{p.max_issues_per_night}</dd>
        </div>
        <div>
          <dt>Services</dt>
          <dd>{p.services.map((s) => s.name).join(", ")}</dd>
        </div>
        <div>
          <dt>Forge token</dt>
          <dd>{p.forge_token_hint ?? "none"}</dd>
        </div>
      </dl>

      <nav className="tabs">
        <button
          type="button"
          className={tab === "issues" ? "active" : ""}
          onClick={() => setTab("issues")}
        >
          Issues
        </button>
        <button
          type="button"
          className={tab === "runs" ? "active" : ""}
          onClick={() => setTab("runs")}
        >
          Auto Heal runs
        </button>
      </nav>

      {tab === "issues" ? <Issues projectId={projectId} /> : <Runs projectId={projectId} />}
    </article>
  );
}

function Issues({ projectId }: { projectId: string }) {
  const issues = useQuery({
    queryKey: ["issues", projectId],
    queryFn: () => api.projectIssues(projectId),
    // Sentry is a third party and this is a list of bugs, not a live feed.
    staleTime: 60_000,
  });

  if (issues.isPending) return <p className="muted">Fetching issues from Sentry…</p>;
  if (issues.error) return <p className="error">{issues.error.message}</p>;

  // **"Sentry refused us" is not "no issues".** They look identical on a screen
  // and mean opposite things; the second is the one that wastes an afternoon.
  if (issues.data.error) {
    return (
      <div className="panel">
        <p className="error">Sentry did not return issues: {issues.data.error.detail}</p>
        <p className="muted">{issues.data.error.remedy}</p>
      </div>
    );
  }

  if (issues.data.issues.length === 0) {
    return (
      <p className="muted">
        Sentry has no unresolved issues for <code>{issues.data.service}</code>.
      </p>
    );
  }

  return (
    <table>
      <thead>
        <tr>
          <th>Issue</th>
          <th>Culprit</th>
          <th className="num">Events</th>
          <th className="num">Users</th>
          <th>Last seen</th>
          <th>Auto Heal</th>
        </tr>
      </thead>
      <tbody>
        {issues.data.issues.map((issue) => (
          <IssueRow
            key={issue.id}
            projectId={projectId}
            serviceName={issues.data.service}
            issue={issue}
          />
        ))}
      </tbody>
    </table>
  );
}

function IssueRow({
  projectId,
  serviceName,
  issue,
}: {
  projectId: string;
  serviceName: string;
  issue: SentryIssue;
}) {
  const navigate = useNavigate();
  const queries = useQueryClient();
  // **Generated once per row, not once per click.** A fresh key on every call
  // defeats the server's idempotency guard entirely: it collapses two requests
  // carrying the *same* key onto one run, so a key that is never repeated can
  // never collapse anything. Held in a ref so a click whose response is lost to
  // the network can be retried onto the same run instead of starting a second.
  const key = useRef(newId());
  const heal = useMutation({
    mutationFn: () =>
      api.trigger({
        project_id: projectId,
        service_name: serviceName,
        // Live: a dry run would score the issue and dispatch nothing, which is
        // not what "Auto Heal" means. What it may then *do* to the repository
        // is the project's write mode, shown above.
        dry_run: false,
        sentry_issue_id: issue.id,
        idempotency_key: key.current,
      }),
    onSuccess: (result) => {
      queries.invalidateQueries({ queryKey: ["runs"] });
      // **The issue list too.** It is what decides whether this button is
      // offered at all, and it is cached for a minute — so without this, coming
      // back to the tab within that minute shows the issue as untouched and
      // offers to heal it a second time. The `in_flight` guard below is only as
      // fresh as the data behind it.
      queries.invalidateQueries({ queryKey: ["issues", projectId] });
      navigate(`/runs/${result.run_id}`);
    },
  });

  return (
    <tr>
      <td>
        <a href={issue.permalink} target="_blank" rel="noreferrer noopener">
          {issue.short_id}
        </a>
        <div>{issue.title}</div>
        {issue.error_type ? <code className="muted">{issue.error_type}</code> : null}
      </td>
      <td className="muted">{issue.culprit}</td>
      <td className="num">{issue.count.toLocaleString()}</td>
      <td className="num">{issue.user_count.toLocaleString()}</td>
      <td className="muted">{issue.last_seen?.slice(0, 10)}</td>
      <td>
        {/* What the engine already knows, so the button never offers to start
            work that is finished or in flight — a duplicate change request
            starts with someone clicking twice. */}
        {issue.in_flight ? (
          <Link to={`/runs/${issue.last_run_id}`}>in progress →</Link>
        ) : issue.change_request_url ? (
          <a href={issue.change_request_url} target="_blank" rel="noreferrer noopener">
            change request →
          </a>
        ) : (
          <>
            <button type="button" onClick={() => heal.mutate()} disabled={heal.isPending}>
              {heal.isPending ? "Starting…" : "Auto Heal"}
            </button>
            {issue.last_status ? (
              <div className="muted">
                last: <Status value={issue.last_status} /> {issue.last_reason ?? ""}
              </div>
            ) : null}
          </>
        )}
        {heal.error ? <div className="error">{heal.error.message}</div> : null}
      </td>
    </tr>
  );
}

function Runs({ projectId }: { projectId: string }) {
  const runs = useQuery({
    queryKey: ["project-runs", projectId],
    queryFn: () => api.projectRuns(projectId),
    refetchInterval: (query) =>
      query.state.data?.some((run) => !run.is_terminal) ? 5_000 : false,
  });

  if (runs.isPending) return <p className="muted">Loading runs…</p>;
  if (runs.error) return <p className="error">{runs.error.message}</p>;
  if (runs.data.length === 0) {
    return <p className="muted">No runs yet. Heal an issue, or wait for the schedule.</p>;
  }

  return (
    <table>
      <thead>
        <tr>
          <th>Run</th>
          <th>Started</th>
          <th>Trigger</th>
          <th>Status</th>
          <th>Reason</th>
          <th className="num">Considered</th>
          <th className="num">Selected</th>
          <th className="num">Cost</th>
        </tr>
      </thead>
      <tbody>
        {runs.data.map((run) => (
          <tr key={run.id}>
            <td>
              <Link to={`/runs/${run.id}`}>{run.id.slice(0, 8)}</Link>
            </td>
            <td className="muted">{run.started_at.slice(0, 16).replace("T", " ")}</td>
            <td>{run.dry_run ? `${run.trigger} (dry)` : run.trigger}</td>
            <td>
              <Status value={run.status} />
            </td>
            <td className="muted">{run.reason ?? "—"}</td>
            <td className="num">{run.considered}</td>
            <td className="num">{run.selected}</td>
            <td className="num">{money(run.total_cost_usd)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
